import React, { useState, useEffect } from 'react';
import { Tag, Button, Icon, Dialog, DialogBody, DialogFooter, Callout, Intent, Spinner } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Suggest, ItemRenderer } from '@blueprintjs/select';
import { observer } from 'mobx-react-lite';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { githubApi } from '../../api/github';
import type { RepoLink, LinkedRepository } from '../../types/linkTypes';
import './RepoLinks.css';

// Re-export the types from the shared file for backward compatibility
export type { RepoLink, LinkedRepository } from '../../types/linkTypes';

// GitHub repository interface
interface GitHubRepository {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  description: string | null;
  owner_login: string;
}

interface RepoLinksProps {
  links?: RepoLink[]; // Keep for backward compatibility
  linkedRepositories?: LinkedRepository[]; // New prop for GitHub repositories
  onAddRepo?: () => void; // Keep for backward compatibility
  onRepositoriesChange?: (repositories: LinkedRepository[]) => void; // New prop for handling repository changes
  nodeId?: string; // Node ID for storing repository links
  mindmapId?: string; // Mindmap ID for storing repository links
}

const RepoLinks: React.FC<RepoLinksProps> = observer(({ 
  links = [], 
  linkedRepositories = [], 
  onAddRepo, 
  onRepositoriesChange,
  nodeId,
  mindmapId 
}) => {
  const appStore = useAppStore();
  const { mindmap } = appStore;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [repositories, setRepositories] = useState<GitHubRepository[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepository | null>(null);
  const [confirmRemoveRepo, setConfirmRemoveRepo] = useState<LinkedRepository | null>(null);

  console.log('[RepoLinks] Rendered with linkedRepositories:', linkedRepositories);

  const getRepoIcon = (type: string) => {
    switch (type) {
      case 'frontend':
        return IconNames.CODE_BLOCK;
      case 'engine':
        return IconNames.COG;
      case 'deployment':
        return IconNames.CLOUD_DOWNLOAD;
      default:
        return IconNames.GIT_REPO;
    }
  };

  // Load repositories when dialog opens
  useEffect(() => {
    if (isDialogOpen && repositories.length === 0) {
      loadRepositories();
    }
  }, [isDialogOpen]);

  const loadRepositories = async () => {
    console.log('[RepoLinks] Loading repositories...');
    setLoading(true);
    setError(null);
    try {
      // Check if we have cached repositories in the store
      const cachedRepos = mindmap.getCachedRepositories();
      if (cachedRepos && cachedRepos.length > 0) {
        console.log('[RepoLinks] Using cached repositories from store:', cachedRepos.length);
        setRepositories(cachedRepos);
        return;
      }
      
      const response = await githubApi.listRepositories();
      console.log('[RepoLinks] Loaded repositories response:', response);
      
      // Safely handle the response - ensure it's an array
      let repos = [];
      if (Array.isArray(response)) {
        repos = response;
      } else if (response && typeof response === 'object' && Array.isArray(response.items)) {
        repos = response.items;
      } else if (response && Array.isArray(response)) {
        repos = response;
      } else {
        repos = [];
      }
      
      console.log('[RepoLinks] Number of repositories loaded:', repos.length);
      
      // Ensure repos is an array before mapping
      if (!Array.isArray(repos)) {
        console.error('[RepoLinks] repos is not an array:', repos);
        setError('Failed to load repositories: Invalid response format');
        setLoading(false);
        return;
      }
      
      // Map to expected format
      const formattedRepos = repos.map(r => ({
        id: r.id,
        name: r.name,
        full_name: r.fullName || r.full_name || `${r.owner}/${r.name}`,
        description: r.description,
        url: r.url || r.html_url,
        html_url: r.url || r.html_url,
        language: r.language,
        stars: r.stars,
        owner: r.owner,
        private: r.isPrivate,
      }));
      
      // Sort repositories by name for consistent ordering
      const sortedRepos = formattedRepos.sort((a, b) => a.name.localeCompare(b.name));
      
      // Cache in store for future opens
      mindmap.setCachedRepositories(sortedRepos);
      
      setRepositories(sortedRepos);
    } catch (err: any) {
      const errorMessage = `Failed to load repositories: ${err.message || 'Unknown error'}`;
      console.error('[RepoLinks] Error loading repositories:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRepository = async () => {
    if (!selectedRepo || !onRepositoriesChange) return;

    // Check if already linked
    if (linkedRepositories.some(repo => repo.id === selectedRepo.id)) {
      setError('This repository is already linked to this node.');
      return;
    }

    const newLinkedRepo: LinkedRepository = {
      id: selectedRepo.id,
      name: selectedRepo.name,
      full_name: selectedRepo.full_name,
      html_url: selectedRepo.html_url || selectedRepo.url,
      description: selectedRepo.description || undefined
    };

    try {
      // Link via store action if nodeId is available
      if (nodeId) {
        const result = await mindmap.linkRepositoryToNode(nodeId, {
          name: selectedRepo.name,
          owner: selectedRepo.owner,
          fullName: selectedRepo.full_name,
          full_name: selectedRepo.full_name,
          url: selectedRepo.html_url || selectedRepo.url,
          html_url: selectedRepo.html_url || selectedRepo.url,
          description: selectedRepo.description,
          language: selectedRepo.language,
          stars: selectedRepo.stars,
          isPrivate: selectedRepo.private,
          private: selectedRepo.private,
        });
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to link repository');
        }
      }

      const updatedRepos = [...linkedRepositories, newLinkedRepo];
      
      // Update repositories via callback
      onRepositoriesChange?.(updatedRepos);
      setSelectedRepo(null);
      setIsDialogOpen(false);
    } catch (err: any) {
      setError(`Failed to link repository: ${err.message || 'Unknown error'}`);
      console.error('[RepoLinks] Error linking repository:', err);
    }
  };

  const handleRemoveRepository = (repo: LinkedRepository) => {
    setConfirmRemoveRepo(repo);
  };

  const confirmRemoveRepository = async () => {
    if (!confirmRemoveRepo) return;
    
    try {
      // Call store method to delete the link if linkId is available
      if (confirmRemoveRepo.linkId) {
        console.log('[RepoLinks] Deleting repository link:', confirmRemoveRepo.linkId);
        const result = await mindmap.removeGithubLink(confirmRemoveRepo.linkId);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to remove repository link');
        }
        
        console.log('[RepoLinks] Repository unlinked successfully');
      }
      
      // The store will automatically update and trigger re-render via observer
      setConfirmRemoveRepo(null);
      setError(null); // Clear any previous errors
      console.log('[RepoLinks] Removed repository:', confirmRemoveRepo.name);
    } catch (error: any) {
      console.error('[RepoLinks] Error removing repository:', error);
      setError(`Failed to remove repository: ${error.message || 'Unknown error'}`);
      // Don't close the dialog on error so user can see the error message
    }
  };

  const cancelRemoveRepository = () => {
    setConfirmRemoveRepo(null);
  };

  const handleRepositoryClick = (repoUrl: string) => {
    window.open(repoUrl, '_blank', 'noopener,noreferrer');
  };

  const handleAddClick = () => {
    if (onRepositoriesChange) {
      // Use new GitHub integration
      setIsDialogOpen(true);
    } else if (onAddRepo) {
      // Fall back to old callback for backward compatibility
      onAddRepo();
    }
  };

  // Filter out already linked repositories
  const availableRepositories = repositories.filter(
    repo => !linkedRepositories.some(linked => linked.id === repo.id)
  );

  console.log('[RepoLinks] Total repositories:', repositories.length);
  console.log('[RepoLinks] Linked repositories:', linkedRepositories.length);  
  console.log('[RepoLinks] Available repositories:', availableRepositories.length);

  const renderRepository: ItemRenderer<GitHubRepository> = (repo, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <div
        key={repo.id}
        onClick={handleClick}
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          backgroundColor: modifiers.active ? '#48AFF0' : 'transparent',
          borderRadius: '3px',
          margin: '2px 0',
          transition: 'background-color 0.15s ease'
        }}
      >
        <div style={{ 
          fontWeight: 'bold', 
          color: modifiers.active ? '#FFFFFF' : '#1F2937',
          fontSize: '14px'
        }}>
          {repo.full_name}
        </div>
        {repo.description && (
          <div style={{ 
            fontSize: '12px', 
            color: modifiers.active ? '#E1E8ED' : '#8A9BA8', 
            marginTop: '4px',
            lineHeight: '1.3'
          }}>
            {repo.description}
          </div>
        )}
        <div style={{ 
          fontSize: '11px', 
          color: modifiers.active ? '#D1D8DD' : '#6A7785', 
          marginTop: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>{repo.private ? '🔒 Private' : '🌐 Public'}</span>
        </div>
      </div>
    );
  };

  const filterRepository = (query: string, repo: GitHubRepository) => {
    const lowerQuery = query.toLowerCase();
    return (
      (repo.name && repo.name.toLowerCase().includes(lowerQuery)) ||
      (repo.full_name && repo.full_name.toLowerCase().includes(lowerQuery)) ||
      (repo.description && repo.description.toLowerCase().includes(lowerQuery)) ||
      (repo.owner && repo.owner.toLowerCase().includes(lowerQuery)) ||
      (repo.owner_login && repo.owner_login.toLowerCase().includes(lowerQuery))
    );
  };

  return (
    <div className="repo-links">
      <div className="repo-links-header">
        <h3>Repositories</h3>
        <Button
          icon={IconNames.GIT_REPO}
          minimal
          small
          onClick={handleAddClick}
          text="Add Repo"
          className="add-repo-button"
        />
      </div>
      <div className="repo-links-content">
        {/* Display GitHub linked repositories */}
        {linkedRepositories.map((repo) => (
          <div key={repo.id} className="repo-link-item">
            <div 
              className="repo-link-content" 
              onClick={() => handleRepositoryClick(repo.html_url)}
              title={`Click to open ${repo.full_name} in GitHub`}
            >
              <Icon icon={IconNames.GIT_REPO} />
              <div>
                <div className="repo-link-title">{repo.name}</div>
                {repo.description && (
                  <div className="repo-link-description">
                    {repo.description.length > 60 ? `${repo.description.slice(0, 60)}...` : repo.description}
                  </div>
                )}
              </div>
            </div>
            {onRepositoriesChange && (
              <Button
                icon={IconNames.CROSS}
                minimal
                small
                className="repo-remove-button"
                onClick={() => handleRemoveRepository(repo)}
                title="Remove repository"
                style={{ 
                  marginLeft: '8px',
                  flexShrink: 0
                }}
              />
            )}
          </div>
        ))}
        
        {/* Display legacy links for backward compatibility */}
        {links.map((link) => (
          <div key={link.id} className="repo-link-item">
            <Icon icon={getRepoIcon(link.type)} />
            <div className="repo-link-content">
              <div className="repo-link-title">{link.name}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Repository selection dialog */}
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Link Repository"
        style={{ width: '500px' }}
      >
        <DialogBody>
          {error && (
            <Callout intent={Intent.DANGER} style={{ marginBottom: '15px' }}>
              {error}
            </Callout>
          )}
          
          <p style={{ marginBottom: '15px', color: '#8A9BA8', lineHeight: '1.4' }}>
            Select a repository to link to this node. Linked repositories will appear in the list above.
          </p>

          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Spinner size={30} />
              <div style={{ color: '#8A9BA8', fontSize: '14px' }}>Loading repositories...</div>
            </div>
          ) : availableRepositories.length === 0 ? (
            <Callout intent={Intent.WARNING}>
              {repositories.length === 0 
                ? 'No repositories found. Make sure GitHub is connected and you have access to repositories.'
                : 'All available repositories are already linked to this node.'
              }
            </Callout>
          ) : (
            <Suggest
              items={availableRepositories}
              itemRenderer={renderRepository}
              itemPredicate={filterRepository}
              onItemSelect={(repo) => setSelectedRepo(repo)}
              inputValueRenderer={(repo) => repo ? repo.full_name : ''}
              popoverProps={{ 
                minimal: true,
                matchTargetWidth: true
              }}
              fill
              inputProps={{
                placeholder: 'Search repositories by name, description, or owner...',
                leftIcon: 'search',
                large: false
              }}
              selectedItem={selectedRepo}
              resetOnSelect={true}
              closeOnSelect={true}
              openOnKeyDown={true}
              resetOnClose={true}
              noResults={<div style={{ padding: '16px', textAlign: 'center', color: '#8A9BA8' }}>No repositories found</div>}
              initialContent={
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {availableRepositories.slice(0, 10).map(repo => (
                    <div
                      key={repo.id}
                      onClick={() => setSelectedRepo(repo)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        borderRadius: '3px',
                        margin: '2px 0',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#48AFF0';
                        e.currentTarget.style.color = '#FFFFFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#F5F8FA';
                      }}
                    >
                      <div style={{ 
                        fontWeight: 'bold', 
                        color: '#F5F8FA',
                        fontSize: '14px'
                      }}>
                        {repo.full_name}
                      </div>
                      {repo.description && (
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#8A9BA8', 
                          marginTop: '4px',
                          lineHeight: '1.3'
                        }}>
                          {repo.description}
                        </div>
                      )}
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#6A7785', 
                        marginTop: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span>{repo.private ? '🔒 Private' : '🌐 Public'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              }
            />
          )}

          {selectedRepo && (
            <div style={{ 
              marginTop: '15px', 
              padding: '12px', 
              border: '1px solid #394B59', 
              borderRadius: '3px',
              backgroundColor: '#30404D'
            }}>
              <div style={{ color: '#F5F8FA', fontWeight: 500 }}>
                <strong>Selected:</strong> {selectedRepo.full_name}
              </div>
              {selectedRepo.description && (
                <div style={{ marginTop: '5px', fontSize: '13px', color: '#8A9BA8' }}>
                  {selectedRepo.description}
                </div>
              )}
            </div>
          )}
        </DialogBody>
        
        <DialogFooter
          actions={
            <>
              <Button onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                intent={Intent.PRIMARY}
                onClick={handleAddRepository}
                disabled={!selectedRepo || loading}
              >
                Link Repository
              </Button>
            </>
          }
        />
      </Dialog>

      {/* Repository removal confirmation dialog */}
      <Dialog
        isOpen={!!confirmRemoveRepo}
        onClose={cancelRemoveRepository}
        title="Remove Repository"
        style={{ width: '400px' }}
      >
        <DialogBody>
          <p style={{ color: '#8A9BA8', lineHeight: '1.4', marginBottom: '15px' }}>
            Are you sure you want to remove <strong style={{ color: '#F5F8FA' }}>{confirmRemoveRepo?.name}</strong> from this node?
          </p>
          <p style={{ color: '#6A7785', fontSize: '13px', lineHeight: '1.3' }}>
            This will only remove the link from this node. The repository itself will not be affected.
          </p>
        </DialogBody>
        
        <DialogFooter
          actions={
            <>
              <Button onClick={cancelRemoveRepository}>
                Cancel
              </Button>
              <Button
                intent={Intent.DANGER}
                onClick={confirmRemoveRepository}
                icon={IconNames.TRASH}
              >
                Remove Repository
              </Button>
            </>
          }
        />
      </Dialog>
    </div>
  );
});

export default RepoLinks; 